<template>
  <div>
    <div v-if="licensesError" class="loading-container">
      <img src="common/client/assets/images/img-error-emoji.svg" alt="error" class="error-image-size" />
      <p class="help-text">Whoops, licenses data failed to load! Please contact support.</p>
    </div>
    <vwc-layout v-else column-basis="block" column-spacing="md">
      <div>
        <h4 class="status-title section-title">Account Status</h4>
        <section class="story">
          <div class="story-items">
            <div class="status-header">
              <h3 class="status-title">Status</h3>
              <!-- accessible refresh button next to Status -->
              <button
                type="button"
                class="status-refresh-icon"
                :class="{ 'is-refreshing': isRefreshingSyncStatus }"
                :disabled="isRefreshingSyncStatus"
                aria-label="Refresh account status"
                @click="onRefreshSyncStatusClick"
              >
                <svg
                  class="status-refresh-svg"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <!-- classic circular refresh arrow -->
                  <path
                    fill="currentColor"
                    d="M17.65 6.35A7.95 7.95 0 0 0 12 4a8 8 0 1 0 7.75 9.5 1 1 0 1 0-1.96-.4A6 6 0 1 1 12 6c1.66 0 3.14.69 4.22 1.78L14 10h7V3l-3.35 3.35z"
                  />
                </svg>
              </button>
            </div>

            <div class="status-container">
              <div class="status-row" v-for="(value, key) in displayFields" :key="key">
                <p class="status-key">{{ key }}</p>
                <span class="status-value account-value">
                  {{ value }}
                </span>
              </div>

              <!-- AutoSync Checkbox -->
              <div class="status-row pbx-autosync-row">
                <p id="pbx-auto-sync-label" class="status-key">PBX Auto Sync:</p>
                <label class="pbx-autosync-checkbox-label">
                  <input
                    type="checkbox"
                    class="pbx-autosync-checkbox"
                    :checked="localAutoSyncEnabled"
                    aria-labelledby="pbx-auto-sync-label"
                    @change="onAutoSyncChange($event)"
                  />
                  <span class="pbx-autosync-custom"></span>
                </label>
              </div>

              <!-- PBX Sync button -->
              <div class="status-row pbx-sync-row">
                <p class="status-key">Vonage Business Cloud PBX:</p>
                <button
                  type="button"
                  class="pbx-sync-button"
                  :disabled="isTriggeringPbxSync"
                  @click="onTriggerPbxSyncClick"
                >
                  <span>{{ isTriggeringPbxSync ? 'Syncing' : 'Sync Now' }}</span>
                  <svg v-if="isTriggeringPbxSync" class="pbx-sync-progress" viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false">
                    <circle cx="10" cy="10" r="8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-dasharray="40 12" />
                  </svg>
                </button>
              </div>
            </div>

            <vwc-button
              :label="syncStatus.isSetup ? 'Manage Users' : 'Assign Users'"
              :disabled="portalUrlLoadingState === PORTAL_URL_LOADING_STATE.pending"
              layout="filled"
              type="submit"
              unelevated=""
              @click="syncStatus.acceptedInvitation ? openPortalInNewTab() : showModal()"
            >
              <vwc-circular-progress
                slot="trailingIcon"
                v-if="portalUrlLoadingState === PORTAL_URL_LOADING_STATE.pending"
                indeterminate=""
                density="-7.5"
                progress="0"
              ></vwc-circular-progress>
              <button type="submit" style="display: none"></button>
            </vwc-button>
          </div>
        </section>
      </div>
      <div>
        <h4 class="products-title status-title section-title">Microsoft Teams Products</h4>
        <div v-for="(licenseData, key) in displayLicensesStatus" :key="key">
          <section class="story ms-product">
            <div class="story-items">
              <h3 class="status-title">{{ licenseData.productLabel }}</h3>
              <div class="status-container">
                <div class="status-row">
                  <p class="status-key">Licenses:</p>
                  <p class="status-value">{{ licenseData.total }}</p>
                </div>
                <div class="status-row">
                  <p class="status-key">Currently in use:</p>
                  <p class="status-value">{{ licenseData.used }}</p>
                </div>
              </div>
              <div class="licenses-quantity">
                <vwc-3-number-field class="quantity-field" value="1" min="1" slot="small" />
                <vwc-button
                  label="Add License"
                  class="add-license-button"
                  :disabled="portalUrlLoadingState === PORTAL_URL_LOADING_STATE.pending"
                  layout="filled"
                  type="submit"
                  unelevated=""
                  @click="syncStatus.acceptedInvitation || true ? openPortalStore($event, licenseData.product) : showModal()"
                >
                  <vwc-circular-progress
                    slot="trailingIcon"
                    v-if="portalUrlLoadingState === PORTAL_URL_LOADING_STATE.pending"
                    indeterminate=""
                    density="-7.5"
                    progress="0"
                  ></vwc-circular-progress>
                  <button type="submit" style="display: none"></button>
                </vwc-button>
              </div>
            </div>
          </section>
        </div>
      </div>

      <vwc-dialog heading="Note" scrimclickaction="" ref="beforeFirstLoginModal" closeButton="true">
        <div>
          Please sign in with your O365 Global Admin credentials to initiate the setup. These are only used during setup.
          <br />
          For more information, please read
          <a href="https://businesssupport.vonage.com/articles/answer/Vonage-for-Microsoft-Teams-V" target="_blank">our article on setup</a>
        </div>
        <vwc-button slot="primaryAction" dialogAction="ok" type="submit" layout="filled" @click="getPortalUrl">
          OK
          <button type="submit" style="display: none"></button>
        </vwc-button>
      </vwc-dialog>
    </vwc-layout>
  </div>
</template>

<script>
import '@vonage/vwc-button';
import '@vonage/vwc-layout';
import '@vonage/vwc-circular-progress';
import '@vonage/vwc-note';
import '@vonage/vwc-dialog';

import { get, find } from 'lodash';

import { mapActions, mapGetters } from 'vuex';
import { PORTAL_URL_LOADING_STATE, VBC_FOR_MS_TEAMS_SKU, VBC_VOICE_FOR_MS_TEAMS_SKU, PRODUCT_TO_LABEL } from '../store/consts';
import { format, parseISO } from 'date-fns';

export default {
  name: 'StatusLayout',

  data() {
    return {
      PORTAL_URL_LOADING_STATE,
      ADMIN_PORTAL_URL: process.env.VUE_APP_ADMIN_PORTAL_URL,
      isRefreshingSyncStatus: false,
      isTriggeringPbxSync: false,
      localAutoSyncEnabled: false,
    };
  },

  computed: {
    ...mapGetters(['syncStatus', 'portalUrlLoadingState', 'licensesError']),

    isSetup() {
      return this.syncStatus.isSetup && this.syncStatus.acceptedInvitation;
    },

    displayFields() {
      const placeHolder = '-------';
      return {
        'Teams Last Sync:': (this.syncStatus.lastSyncTime && format(parseISO(this.syncStatus.lastSyncTime), 'MM/dd/yyyy h:mm:ss a')) || placeHolder,
        'Teams Sync Current Status:': this.syncStatus.lastSyncStatus || placeHolder,
        'PBX Last Sync:': (this.syncStatus.pbxLastSyncTime && format(parseISO(this.syncStatus.pbxLastSyncTime), 'MM/dd/yyyy h:mm:ss a')) || placeHolder,
        'PBX Sync Current Status:': this.syncStatus.pbxLastSyncStatus || placeHolder,
        'Account Name:': this.syncStatus.accountName || placeHolder,
      };
    },

    displayLicensesStatus() {
      const msTeamsLicenses = find(this.syncStatus.licenses, (licenses) => licenses.product == VBC_FOR_MS_TEAMS_SKU);
      const vbcVoiceLicenses = find(this.syncStatus.licenses, (licenses) => licenses.product == VBC_VOICE_FOR_MS_TEAMS_SKU);
      return [msTeamsLicenses, vbcVoiceLicenses]
        .filter((licenses) => !!licenses)
        .map((licenses) => {
          licenses.productLabel = PRODUCT_TO_LABEL[licenses.product];
          return licenses;
        });
    },
  },
  watch: {
    // keep local flag in sync with store
    'syncStatus.isAutoSyncEnabled': {
      immediate: true,
      handler(val) {
        this.localAutoSyncEnabled = val === true;
      },
    },
  },

  methods: {
    ...mapActions(['getPortalUrl', 'updatePbxService', 'triggerPbxSync', 'getSyncStatus']),

    showModal() {
      this.$refs.beforeFirstLoginModal.show();
    },

    openPortalInNewTab() {
      window.open(this.syncStatus.portalUrl, '_blank', 'noopener');
    },

    openPortalStore(event, product) {
      const quantity = get(event, 'target.parentNode.childNodes.0.value', 1);
      window.open(`${this.ADMIN_PORTAL_URL}/management/m/addons?itemToFilter=${product}&addProductToCart=true&productQuantity=${quantity}`);
    },

    async onTriggerPbxSyncClick() {
      if (this.isTriggeringPbxSync) return;
      try {
        this.isTriggeringPbxSync = true;
        await this.triggerPbxSync();
        await this.getSyncStatus();
      } finally {
        this.isTriggeringPbxSync = false;
      }
    },

    async onRefreshSyncStatusClick() {
      if (this.isRefreshingSyncStatus) return;
      this.isRefreshingSyncStatus = true;
      await this.getSyncStatus();
      this.isRefreshingSyncStatus = false;
    },

    async onAutoSyncChange(event) {
      const originalChecked = this.localAutoSyncEnabled;
      const newChecked = event.target.checked;
      this.localAutoSyncEnabled = newChecked;
      try {
        await this.updatePbxService({ isAutoSyncEnabled: newChecked });
        await this.getSyncStatus();
      } catch (error) {
        this.localAutoSyncEnabled = originalChecked;
      }
    },
  },
};
</script>

<style>
.loading-container {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.error-image-size {
  width: 81px;
  height: 96px;
}

.help-text {
  font: 300 condensed 14px / 24px SpeziaWebVariable;
  line-height: 20px;
  margin: 0px;
}

.status-title {
  font: 450 condensed 20px / 23px SpeziaWebVariable;
  letter-spacing: -1px;
  display: block;
  margin: 0px;
  padding: 0px;
}

.products-title {
  padding-top: 8px !important;
}

h4.status-title {
  padding: 12px 0 12px 0;
}

.story {
  background-color: var(--vvd-color-neutral-10);
  color: var(--on-connotation);
  border-radius: 6px;
  overflow: hidden;
  box-shadow: inset 0 0 0 1px var(--vvd-color-neutral-20);
  padding-top: 0;
  box-sizing: border-box;
  max-width: min(80%, 520px);
}

.status-row {
  display: flex;
  flex-flow: row wrap;
  align-items: stretch;
}

.status-row p {
  line-height: 0;
}

.status-key {
  font: 400 ultra-condensed 14px / 24px SpeziaWebVariable;
  flex: 0 0 60%;
  max-width: 60%;
  color: var(--vvd-color-neutral-70);
}

.status-value {
  font: 600 ultra-condensed 14px / 24px SpeziaWebVariable;
  flex: 0 0 40%;
  max-width: 40%;
}

.account-value {
  overflow: auto;
}

.status-container {
  margin: 16px 0 16px 0;
}

.section-title {
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
}

.ms-product {
  margin-bottom: 20px;
}

.story-items {
  padding: 24px;
}

vwc-button {
  font-size: 14px;
}

vwc-button.add-license-button {
  border: 1px solid;
  border-radius: 7px;
}

.licenses-quantity {
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  padding: 0px;
  gap: 12px;
}

.quantity-field {
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  border: 1px solid #929292;
  border-radius: 6px;
  width: 110px;
  font: 400 ultra-condensed 14px / 24px SpeziaWebVariable;
}


.pbx-sync-button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0 14px;
  height: 40px;
  border: 2px solid #000;
  border-radius: 6px;
  background: transparent;
  -webkit-appearance: none;
  appearance: none;
  color: #000;
  font-size: 12px;
  font-family: SpeziaWebVariable, sans-serif;
  font-weight: 700;
  font-stretch: condensed;
  cursor: pointer;
  box-sizing: border-box;
}

.pbx-sync-button:disabled {
  border: 1px solid #929292;
  color: #929292;
  font-weight: 400;
  cursor: not-allowed;
  pointer-events: none;
  opacity: 0.6;
}

.pbx-sync-progress {
  width: 14px;
  height: 14px;
  animation: pbx-spin 0.8s linear infinite;
  flex-shrink: 0;
}

@keyframes pbx-spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}

.pbx-autosync-row {
  align-items: center;
  margin-top: 8px;
  margin-bottom: 12px;
}
.pbx-autosync-checkbox-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-left: 8px;
  position: relative;
}
.pbx-autosync-checkbox {
  opacity: 0;
  width: 0;
  height: 0;
  position: absolute;
}
.pbx-autosync-custom {
  width: 22px;
  height: 22px;
  border: 2px solid #929292;
  border-radius: 5px;
  background: #fff;
  display: inline-block;
  position: relative;
  transition: border-color 0.2s;
}
.pbx-autosync-checkbox:checked + .pbx-autosync-custom {
  border-color: #871eff;
  background: #f3eaff;
}
.pbx-autosync-custom:after {
  content: '';
  position: absolute;
  display: none;
}
.pbx-autosync-checkbox:checked + .pbx-autosync-custom:after {
  display: block;
}
.pbx-autosync-custom:after {
  left: 6px;
  top: 2px;
  width: 6px;
  height: 12px;
  border: solid #871eff;
  border-width: 0 3px 3px 0;
  transform: rotate(45deg);
  content: '';
}
.pbx-autosync-checkbox:focus-visible + .pbx-autosync-custom {
  outline: 3px solid #871eff;
  outline-offset: 2px;
  box-shadow: 0 0 0 2px rgba(135, 30, 255, 0.25);
}

/* Optional: avoid double focus indicator in browsers that apply focus styles differently */
.pbx-autosync-checkbox:focus:not(:focus-visible) + .pbx-autosync-custom {
  outline: none;
  box-shadow: none;
}

.status-header {
  display: flex;
  align-items: center;
  gap: 8px;
}

.status-refresh-icon {
  cursor: pointer;
  line-height: 1;
  user-select: none;
  padding: 4px;
  border-radius: 4px;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease,
    transform 0.15s ease;
  border: 1px solid transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: none;
}
/* remove default button appearance for better styling consistency */
.status-refresh-icon {
  -webkit-appearance: none;
  appearance: none;
}

.status-refresh-svg {
  width: 16px;
  height: 16px;
}

/* spin while refreshing */
.status-refresh-icon.is-refreshing .status-refresh-svg {
  animation: status-refresh-spin 0.8s linear infinite;
}

.status-refresh-icon.is-refreshing {
  pointer-events: none;
  opacity: 0.6; 
}

@keyframes status-refresh-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.status-refresh-icon:hover {
  background-color: rgba(0, 0, 0, 0.04);
  border-color: #929292;
  transform: scale(1.05);
}
</style>